import { useMemo } from 'react'
import { Popover, Typography } from 'antd'
import type { Components } from 'react-markdown'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { KnowledgeScope } from '../constants/knowledgeScope'

export type CitationRef = {
  cite_index?: number
  source_type?: 'kb' | 'web'
  item_id?: number
  title?: string
  chunk_index?: number
  snippet?: string
  url?: string
}

type PreviewTarget = { itemId: number; scope: KnowledgeScope }

type Props = {
  content: string
  citationRefs: CitationRef[]
  defaultScope?: KnowledgeScope
  onPreview?: (target: PreviewTarget) => void
}

const CITATION_RE = /\[citation:(\d{1,2})\]/gi

function hasCitationMarkers(text: string): boolean {
  CITATION_RE.lastIndex = 0
  return CITATION_RE.test(text)
}

function refByIndex(refs: CitationRef[], n: number): CitationRef | undefined {
  const direct = refs.find((r) => r.cite_index === n)
  if (direct) return direct
  if (n >= 1 && n <= refs.length) return refs[n - 1]
  return undefined
}

/** 将 [citation:N] 转为 Markdown 链接，避免拆段破坏 GFM 表格。 */
function preprocessCitationLinks(text: string): string {
  return text.replace(CITATION_RE, '[$1](#cite-$1)')
}

function KbCitationMark({
  n,
  refItem,
  defaultScope,
  onPreview,
}: {
  n: number
  refItem: CitationRef | undefined
  defaultScope: KnowledgeScope
  onPreview?: (target: PreviewTarget) => void
}) {
  const title = refItem?.title?.trim() || `文档片段 ${n}`
  const snippet = refItem?.snippet?.trim()
  const chunkNo = (refItem?.chunk_index ?? 0) + 1
  const itemId = refItem?.item_id

  const popover = (
    <div className="chat-cite-popover">
      <Typography.Text strong>{title}</Typography.Text>
      {chunkNo > 0 ? (
        <Typography.Text type="secondary" className="chat-cite-popover-meta">
          片段 {chunkNo}
        </Typography.Text>
      ) : null}
      {snippet ? <div className="chat-cite-popover-snippet">{snippet}</div> : null}
      {itemId && onPreview ? (
        <button
          type="button"
          className="chat-cite-popover-link"
          onClick={() => onPreview({ itemId, scope: defaultScope })}
        >
          查看原文档
        </button>
      ) : null}
    </div>
  )

  return (
    <Popover content={popover} trigger="click" placement="top">
      <sup className="chat-cite" title={`来源：${title}`}>
        {n}
      </sup>
    </Popover>
  )
}

function WebCitationMark({ n, refItem }: { n: number; refItem: CitationRef | undefined }) {
  const url = refItem?.url?.trim()
  const title = refItem?.title?.trim() || url || `网页 ${n}`

  if (!url) {
    return (
      <sup className="chat-cite chat-cite--missing" title="未匹配到网页来源">
        {n}
      </sup>
    )
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="chat-cite"
      title={`查看原文：${title}`}
    >
      {n}
    </a>
  )
}

function CitationMark({
  n,
  refItem,
  defaultScope,
  onPreview,
}: {
  n: number
  refItem: CitationRef | undefined
  defaultScope: KnowledgeScope
  onPreview?: (target: PreviewTarget) => void
}) {
  if (refItem?.source_type === 'web' || refItem?.url) {
    return <WebCitationMark n={n} refItem={refItem} />
  }
  return (
    <KbCitationMark
      n={n}
      refItem={refItem}
      defaultScope={defaultScope}
      onPreview={onPreview}
    />
  )
}

/** 整段 Markdown 渲染，表格内 [citation:N] 转为可点击上标。 */
export function ChatAnswerWithCitations({
  content,
  citationRefs,
  defaultScope = 'enterprise',
  onPreview,
}: Props) {
  const text = content || ''
  const md = useMemo(() => preprocessCitationLinks(text), [text])

  const components = useMemo((): Components => {
    return {
      a: ({ href, children }) => {
        if (href?.startsWith('#cite-')) {
          const n = parseInt(href.slice(6), 10)
          if (Number.isFinite(n)) {
            return (
              <CitationMark
                n={n}
                refItem={refByIndex(citationRefs, n)}
                defaultScope={defaultScope}
                onPreview={onPreview}
              />
            )
          }
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        )
      },
    }
  }, [citationRefs, defaultScope, onPreview])

  if (!text.trim()) return null

  if (!citationRefs.length || !hasCitationMarkers(text)) {
    return (
      <div className="chat-markdown">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
      </div>
    )
  }

  return (
    <div className="chat-answer-with-cites">
      <div className="chat-markdown">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {md}
        </ReactMarkdown>
      </div>
    </div>
  )
}

export { hasCitationMarkers }
